// Pending invitations from GET /events/invites/?include_occurrences=1. Two
// independent kinds, each answered at its own endpoint:
//   series      { id, scope: "series", event, occurrence: null, ... }
//               id is the series RSVP's; answered with POST /events/{id}/rsvp/
//   occurrence  { id, scope: "occurrence", event: null, occurrence, ... }
//               id is the occurrence RSVP's; answered with
//               POST /event-occurrences/{occurrence.id}/rsvp/. The invitee
//               can't see the series: `event` is null, recurrence is masked.
// IDs come from two tables, so an invitation is identified by (scope, id),
// never by id alone. A series and an occurrence invitation to the same Event
// can both be pending; answering one never answers the other. Items without a
// `scope` (the default list, or older responses) are series invitations.

import { effectiveOccurrence } from "./eventDomain";

export const INVITE_SCOPE = { SERIES: "series", OCCURRENCE: "occurrence" };

export function inviteScope(invite) {
  return invite?.scope === INVITE_SCOPE.OCCURRENCE ? INVITE_SCOPE.OCCURRENCE : INVITE_SCOPE.SERIES;
}

// Stable identity / React key.
export function inviteKey(invite) {
  return `${inviteScope(invite)}-${invite?.id}`;
}

export function isOccurrenceInvite(invite) {
  return inviteScope(invite) === INVITE_SCOPE.OCCURRENCE;
}

// The Event an invitation belongs to (an occurrence invitation's via its
// occurrence — for matching only; that Event isn't visible to the invitee).
export function inviteEventId(invite) {
  return isOccurrenceInvite(invite) ? invite.occurrence?.event_id ?? null : invite?.event?.id ?? null;
}

// What an inbox row shows, whichever kind it is: a series invitation shows
// the Event; an occurrence invitation its occurrence's effective values, and
// nothing about the series.
export function inviteDetails(invite) {
  if (isOccurrenceInvite(invite)) {
    const occurrence = invite.occurrence || {};
    const details = effectiveOccurrence({ occurrence });
    return {
      title: details.title,
      starts_at: details.starts_at,
      ends_at: details.ends_at,
      is_all_day: details.is_all_day,
      timezone: details.timezone,
      hostedBy: occurrence.creator?.username ?? null,
    };
  }
  const event = invite?.event || {};
  return {
    title: event.title ?? "",
    starts_at: event.starts_at,
    ends_at: event.ends_at,
    is_all_day: Boolean(event.is_all_day),
    timezone: event.timezone,
    hostedBy: event.hosted_by ?? null,
  };
}

// The inbox badge: every pending invitation, of both kinds, each counted once.
export function pendingInviteCount(invites) {
  return new Set((invites || []).filter((i) => i.status === "invited").map(inviteKey)).size;
}

// Without the one answered invitation — matched by (scope, id).
export function withoutInvite(invites, invite) {
  const key = inviteKey(invite);
  return (invites || []).filter((i) => inviteKey(i) !== key);
}

// After the viewer answered an occurrence (or lost access to it): its
// pending occurrence invitation is resolved. Series invitations stay.
export function withoutOccurrenceInvites(invites, occurrenceId) {
  return (invites || []).filter((i) => !(isOccurrenceInvite(i) && i.occurrence?.id === occurrenceId));
}

// After the viewer answered the series: its series invitation is resolved.
// Occurrence invitations to that Event stay pending.
export function withoutSeriesInvites(invites, eventId) {
  return (invites || []).filter((i) => !(!isOccurrenceInvite(i) && i.event?.id === eventId));
}

// The Event was deleted: every invitation to it, of both kinds, is gone.
export function withoutEventInvites(invites, eventId) {
  return (invites || []).filter((i) => inviteEventId(i) !== eventId);
}

// ---------------------------------------------------------------------------
// The organizer's invitations from an Event's detail.
// ---------------------------------------------------------------------------

// One invitation's outcome, for inviteSummary: from a successful response
// (`invited`: false = a no-op — already pending, or already going, for a date
// here or through the series), or from the error.
export function inviteOutcome({ invited = null, error = null }) {
  if (!error) return { kind: invited === false ? "alreadyIncluded" : "invited" };
  const res = error.response;
  if (res?.status === 409 && res.data?.code === "occurrence_canceled") return { kind: "canceled" };
  // The occurrence endpoint's block carries a code; the series one is a bare 403.
  if (res?.status === 403) return { kind: "notAllowed", detail: res.data?.detail || null };
  return { kind: "failed" };
}

// What happened to the invitations sent at one scope ("series" /
// "occurrence"), summarized for the organizer (iOS InviteSummary). A no-op is
// reported as such — never as a new invitation. → { title, message }
export function inviteSummary(scope, outcomes) {
  const count = (kind) => outcomes.filter((o) => o.kind === kind).length;
  if (outcomes.some((o) => o.kind === "canceled")) {
    return { title: "Date Canceled", message: "This date was canceled, so no one can be invited to it." };
  }
  const invited = count("invited");
  const already = count("alreadyIncluded");
  const notAllowed = count("notAllowed");
  const failed = count("failed");
  const parts = [];
  if (invited > 0) {
    const target = scope === INVITE_SCOPE.SERIES ? "the series" : "this date";
    parts.push(`Invited ${invited} ${invited === 1 ? "person" : "people"} to ${target}.`);
  }
  if (already > 0) {
    parts.push(already === 1 ? "1 person was already invited or going." : `${already} people were already invited or going.`);
  }
  if (notAllowed > 0) {
    const detail = outcomes.find((o) => o.kind === "notAllowed" && o.detail)?.detail;
    parts.push(notAllowed === 1 ? detail || "1 person couldn't be invited." : `${notAllowed} people couldn't be invited.`);
  }
  if (failed > 0) {
    parts.push(
      failed === 1
        ? "1 invitation couldn't be sent. Please try again."
        : `${failed} invitations couldn't be sent. Please try again.`
    );
  }
  return { title: invited > 0 ? "Invitations Sent" : "No Invitations Sent", message: parts.join(" ") };
}
